import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Image, Modal, Spinner, Table, Alert, NavLink } from "react-bootstrap";
import { fetchWithAuth } from "../../Functions";
import plus from "../../assets/images/plus.svg";


const ManageReviews = () => {
    const API = process.env.REACT_APP_API_BASE_URL;
    const [reviews, setReviews] = useState([]);
    const [fetchFeedback, setFetchFeedback] = useState({ success: null, message: "" });
    const [deleteFeedBack, setDeleteFeedback] = useState({ success: null, message: "" });
    const [isLoading, setIsLoading] = useState(null);
    const navigate = useNavigate();

    // Fedback popup for delete review
    const [showModal, setShowModal] = useState(false);
    const handleModalClose = () => {
        setShowModal(false);
    }

    // Fetch review logic
    const fetchReviews = useCallback(async () => {
        setIsLoading(true);

        try {
            const response = await fetchWithAuth(`${API}/reviews/`);
            const data = await response.json();

            // Handle 401
            if (response.status === 401) {
                console.error("Session expired. Redirecting to admin login page.");

                // Set a message to inform the user before the redirect
                setFetchFeedback({
                    success: false,
                    message: "Your session has expired. Please log in again."
                });

                // Navigate to admin login page
                navigate("/admin", { replace: true });

                // Stop further execution of the function
                return; 
            }

            // Handle other unsuccessful responses
            if (!response.ok) {
                setFetchFeedback({ success: false, message: "Unable to load reviews, please refresh page."});
                console.error(`Error fetching reviews: ${data?.detail}` || `Error fetching reviews: ${data?.status}`);
            }

            setReviews(data);
            setFetchFeedback({ success: true, message: ""});
        } catch (error) {
            setFetchFeedback({ success: false, message: "An unexpected error occured fetching reviews."});
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    }, [API, navigate]);

    // Delete review logic
    const deleteReview = async (reviewID, reviewerName) => {
        try {
            const response = await fetchWithAuth(`${API}/reviews/delete/${reviewID}/`, {
                method: 'DELETE',
            });

            // Handle 401
            if (response.status === 401) {
                console.error("Session expired. Redirecting to admin login page.");

                // Set a message to inform the user before the redirect
                setDeleteFeedback({
                    success: false,
                    message: "Your session has expired. Please log in again."
                });

                // Navigate to admin login page
                navigate("/admin", { replace: true });

                // Stop further execution of the function
                return; 
            }

            // Handle delete fail
            if (!response.ok) {
                const errorData = await response.json();
                console.log("Got here: not response ok block");
                setDeleteFeedback({ success: false, message: `Unable to delete ${reviewerName}'s review, please try again.` });
                console.error(`Error deleting review: ${errorData?.detail}` || `Error deleting review: ${errorData?.status}`);
            }

            // Remove review from page
            const updatedReviews = reviews.filter((review) => review.id !== reviewID);
            setReviews(updatedReviews);

            setDeleteFeedback({ success: true, message: `Review by ${reviewerName} successfully deleted!` });
        } catch (error) {
            setDeleteFeedback({ success: false, message: `An unexpected error occured deleting ${reviewerName}'s review.` });
            console.error(error);
        }

        // Activate popup to display delete feedback
        setShowModal(true);
    }

    // Update review logic
    const handleReviewUpdate = async (reviewID) => {
        navigate("/dashboard/manage-reviews/edit/", { state: {reviewID} });
    }

    // Load reviews into the page
    useEffect(() => {
        fetchReviews();
    }, [fetchReviews]);

    return (
        <>
            <h1 className="my-lg-3">Manage Reviews</h1>

            <Button className="px-4 py-2 my-3" href="/dashboard/manage-reviews/add">
                <Image src={plus} /> Add Review
            </Button>

            {/* Fetch Reviews Alert */}
            { fetchFeedback.success === false && // Display this alert only when fetchReviews fails
                <Alert variant="danger" className="text-center">{ fetchFeedback.message }</Alert>
            }

            {isLoading ? (
                <div className="text-center mt-5">
                    <Spinner animation="border" variant="primary" className="mx-auto" />
                    <p className="mt-2">Loading reviews...</p>
                </div>
            ) : (
                reviews && (
                    <Table className="responsive-table my-3">
                        <thead>
                            <tr>
                                <th>Reviewer</th>
                                <th>Type</th>
                                <th>Comment</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            { reviews.map((review, index) => (
                                <tr key={ index }>
                                    <td data-label="Reviewer">
                                        <NavLink href="/#reviews" className="text-decoration-underline text-primary">{ review.name }</NavLink>
                                    </td>
                                    <td data-label="Type">{ review.reviewer_type }</td>
                                    <td data-label="Comment" className="listing-name">{ review.comment.slice(0, 50) }...</td>
                                    <td data-label="Actions">
                                        <div>
                                            <Button className="me-2" variant="warning" onClick={() => handleReviewUpdate(review.id)}>Edit</Button>
                                            <Button variant="danger" onClick={ () => deleteReview(review.id, review.name)}>Delete</Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        { deleteFeedBack.success !== null &&
                            <Modal show={ showModal } onHide={ handleModalClose } size="sm" centered>
                                <Modal.Header closeVariant="danger" closeButton>
                                    <Modal.Title as="h5">{ deleteFeedBack.success ? "Success" : "Failed!"}</Modal.Title>
                                </Modal.Header>
                                <Modal.Body as="p" className="fw-semibold">{ deleteFeedBack.message }</Modal.Body>
                            </Modal>
                        }
                    </Table>
                )
            )}
        </>
    )
}

export default ManageReviews;
